import { IServerSideDatasource } from 'ag-grid-enterprise';

import { apiClient } from './api-client';
import { queryClient } from './query-client';

/**
 * Factory to create a reusable AG Grid server-side datasource using fetch.
 * Handles sorting, filtering, pagination, and error handling with advanced filter support.
 *
 * @param url - The full API endpoint URL for the resource.
 * @param getRequestBody - Optional function to customize the request body.
 * @returns IServerSideDatasource for AG Grid
 */
interface AgGridPaginatedResponse<T> {
  total: number;
  records: T[];
}

// ---------------------------------------------------------------------------
// AG Grid filter model shapes
// AG Grid serialises set-filter values as strings regardless of the source
// type, so all set/legacy filter configs carry string[] values.
// ---------------------------------------------------------------------------

interface AgTextFilterConfig {
  filterType: 'text';
  type: string;
  filter?: string;
}

interface AgNumberFilterConfig {
  filterType: 'number';
  type: string;
  filter: number | null;
  filterTo: number | null;
}

interface AgDateFilterConfig {
  filterType: 'date';
  type: string;
  dateFrom: string | null;
  dateTo: string | null;
}

/** AG Grid Set Filter — values are always serialised as strings by AG Grid */
interface AgSetFilterConfig {
  filterType: 'set';
  values: string[];
}

/**
 * Legacy set filter format that has no `filterType` discriminant.
 * `filterType?: never` ensures it is excluded from the discriminated branches.
 */
interface AgLegacySetFilterConfig {
  filterType?: never;
  values: string[];
}

type AgFilterConfig =
  | AgTextFilterConfig
  | AgNumberFilterConfig
  | AgDateFilterConfig
  | AgSetFilterConfig
  | AgLegacySetFilterConfig;

// ---------------------------------------------------------------------------
// Parsed output types sent to the API
// ---------------------------------------------------------------------------

/** A single filter condition in the API request body */
export interface FilterCondition {
  operator: string;
  value: string | number | boolean | null;
  /**
   * Inclusive range start for `between` date conditions.
   * Only populated when `operator === 'between'`; absent for all other operators.
   */
  start_value?: string | null;
  /**
   * Inclusive range end for `between` date conditions.
   * Only populated when `operator === 'between'`; absent for all other operators.
   */
  end_value?: string | null;
}

/** A parsed filter entry for one column, sent to the API */
export interface ParsedFilter {
  conditions: FilterCondition[];
  logic: 'and' | 'or';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Operator maps — translate AG Grid's camelCase type strings to the API's
// snake_case operator names.
// ---------------------------------------------------------------------------

const TEXT_OPERATOR_MAP: Record<string, string> = {
  contains: 'contains',
  notContains: 'not_contains',
  equals: 'equals',
  notEqual: 'not_equals',
  startsWith: 'starts_with',
  endsWith: 'ends_with',
  blank: 'blank',
  notBlank: 'not_blank',
};

const NUMBER_OPERATOR_MAP: Record<string, string> = {
  equals: 'equals',
  notEqual: 'not_equals',
  lessThan: 'less_than',
  lessThanOrEqual: 'less_than_or_equal',
  greaterThan: 'greater_than',
  greaterThanOrEqual: 'greater_than_or_equal',
  blank: 'blank',
  notBlank: 'not_blank',
};

const DATE_OPERATOR_MAP: Record<string, string> = {
  equals: 'equals',
  notEqual: 'not_equals',
  // The API only exposes exclusive boundary operators ('before' = <, 'after' = >).
  // There are no on_or_before / on_or_after variants, so lessThanOrEqual and
  // greaterThanOrEqual are approximated with the closest available operator.
  // This is a known API limitation for single-value date comparisons.
  lessThan: 'before',
  lessThanOrEqual: 'before',
  greaterThan: 'after',
  greaterThanOrEqual: 'after',
  // Range filters use the dedicated 'between' operator (inclusive on both ends)
  // handled explicitly in parseFilterModel — this entry is for completeness.
  inRange: 'between',
  blank: 'blank',
  notBlank: 'not_blank',
};

/**
 * AG Grid's Set Filter always serialises values as strings in the filter model,
 * regardless of the types supplied in `filterParams.values`. Pass the column IDs
 * of any boolean fields here so that `"true"`/`"false"` strings are converted
 * back to proper booleans before the payload is sent to the API.
 */
function coerceSetValue(value: string, isBooleanField: boolean): string | boolean {
  if (isBooleanField && (value === 'true' || value === 'false')) {
    return value === 'true';
  }
  return value;
}

/**
 * Parse AG Grid's filter model into the API's query-friendly format.
 * Handles text, number, date, and set filters with their respective operators.
 */
function parseFilterModel(
  filterModel: Record<string, AgFilterConfig | null | undefined>,
  booleanFields: ReadonlySet<string> = new Set(),
): Record<string, ParsedFilter> {
  const filters: Record<string, ParsedFilter> = {};

  Object.entries(filterModel).forEach(([colId, filterConfig]) => {
    if (!filterConfig) return;

    if (filterConfig.filterType === 'text') {
      const { type, filter } = filterConfig;
      const operator = TEXT_OPERATOR_MAP[type] ?? 'contains';
      const isBlankOp = operator === 'blank' || operator === 'not_blank';

      // Blank/not-blank conditions have no value; other conditions require a
      // non-empty filter string before we emit anything.
      if (isBlankOp || filter) {
        filters[colId] = {
          conditions: [{ operator, value: isBlankOp ? null : (filter ?? '') }],
          logic: 'and',
        };
      }
    } else if (filterConfig.filterType === 'number') {
      const { type, filter, filterTo } = filterConfig;
      const operator = NUMBER_OPERATOR_MAP[type] ?? 'equals';
      const isBlankOp = operator === 'blank' || operator === 'not_blank';
      const conditions: FilterCondition[] = [];

      if (type === 'inRange' && filter !== null && filterTo !== null) {
        conditions.push({ operator: 'greater_than_or_equal', value: filter });
        conditions.push({ operator: 'less_than_or_equal', value: filterTo });
      } else if (isBlankOp) {
        conditions.push({ operator, value: null });
      } else if (filter !== null && filter !== undefined) {
        // Check both null and undefined explicitly: AG Grid may set the filter value to either
        // when user clears the input or the filter is not initialized. While JavaScript's == coercion
        // treats them equivalently, TypeScript's strict mode requires both checks to satisfy type guards
        // and ensure we only emit a condition when the user has explicitly entered a numeric value.
        conditions.push({ operator, value: filter });
      }

      if (conditions.length > 0) {
        filters[colId] = { conditions, logic: 'and' };
      }
    } else if (filterConfig.filterType === 'date') {
      const { type, dateFrom, dateTo } = filterConfig;
      const operator = DATE_OPERATOR_MAP[type] ?? 'equals';
      const isBlankOp = operator === 'blank' || operator === 'not_blank';
      const conditions: FilterCondition[] = [];

      if (type === 'inRange' && dateFrom && dateTo) {
        // Use the API's dedicated 'between' operator for date ranges.
        // This sends a single condition with start_value/end_value (inclusive
        // on both boundaries), matching DateFilterCondition in the API schema.
        // Sending two separate 'after'/'before' conditions would be exclusive
        // on both ends, which is incorrect for a range filter.
        conditions.push({
          operator: 'between',
          value: null,
          start_value: dateFrom,
          end_value: dateTo,
        });
      } else if (isBlankOp) {
        conditions.push({ operator, value: null });
      } else if (dateFrom) {
        conditions.push({ operator, value: dateFrom });
      }

      if (conditions.length > 0) {
        filters[colId] = { conditions, logic: 'and' };
      }
    } else if (filterConfig.filterType === 'set') {
      const { values: selectedValues } = filterConfig;
      if (selectedValues.length > 0) {
        const isBooleanField = booleanFields.has(colId);
        filters[colId] = {
          conditions: selectedValues.map(value => ({
            operator: 'equals',
            // AG Grid serialises set-filter values as strings; coerce back to
            // boolean for fields declared as boolean so the API receives the
            // correct primitive type (true / false, not "true" / "false").
            value: coerceSetValue(value, isBooleanField),
          })),
          logic: 'or',
        };
      }
    } else if (Array.isArray(filterConfig.values)) {
      // Fallback for legacy set filter format (no filterType discriminant)
      const { values: selectedValues } = filterConfig;
      if (selectedValues.length > 0) {
        const isBooleanField = booleanFields.has(colId);
        filters[colId] = {
          conditions: selectedValues.map(value => ({
            operator: 'equals',
            value: coerceSetValue(value, isBooleanField),
          })),
          logic: 'or',
        };
      }
    }
  });

  return filters;
}

/**
 * Convert sort model to query parameters
 */
function parseSortModel(sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>): {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
} {
  if (!sortModel || sortModel.length === 0) {
    return { sortBy: 'created_at', sortOrder: 'desc' };
  }

  const firstSort = sortModel[0];
  return {
    sortBy: firstSort.colId,
    sortOrder: firstSort.sort || 'asc',
  };
}

export interface AgGridDatasourceOptions {
  /**
   * Column IDs whose values should be treated as booleans.
   *
   * AG Grid's Set Filter always serialises filter-model values as strings, so
   * `true` becomes `"true"` and `false` becomes `"false"` by the time the model
   * reaches the datasource. Listing a field here causes `parseFilterModel` to
   * convert those strings back to proper booleans before the request is sent.
   *
   * @example { booleanFields: ['is_super_admin', 'is_active'] }
   */
  booleanFields?: string[];

  /**
   * How long (in milliseconds) a cached datasource response is considered fresh.
   *
   * When the grid re-mounts after navigation (e.g. the user goes to another page
   * and comes back), `getRows` will be satisfied instantly from the TanStack Query
   * cache if the data is still within this window — avoiding the loading overlay
   * entirely.  Set to `0` to always re-fetch, or `Infinity` to never re-fetch
   * until the cache is explicitly invalidated.
   *
   * The default is intentionally long (1 day) so that navigation between pages
   * is always instant. Use `useAgGridDatasourceRefresh` to give users an explicit
   * "Refresh" action that invalidates this cache and purges the grid in one call.
   *
   * @default 86_400_000 (1 day)
   */
  staleTime?: number;
}

/**
 * Returns the TanStack Query key prefix used by `createAgGridDatasource` for a
 * given endpoint URL.
 *
 * Pass this to `queryClient.invalidateQueries` to mark every cached page /
 * sort / filter combination for that grid as stale, then call
 * `gridRef.current?.api.refreshServerSide({ purge: true })` to trigger a
 * reload.  `useAgGridDatasourceRefresh` does both steps for you.
 *
 * @example
 * ```typescript
 * queryClient.invalidateQueries({
 *   queryKey: agGridDatasourceQueryKey(userQueryKeys.endpoints.globalSearch()),
 * });
 * ```
 */
export function agGridDatasourceQueryKey(url: string) {
  return ['ag-grid-datasource', url] as const;
}

/** Parameters passed to the optional `getRequestBody` factory */
export interface GetRequestBodyParams {
  skip: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: Record<string, ParsedFilter>;
}

export function createAgGridDatasource<TRecord, TResponse extends AgGridPaginatedResponse<TRecord>>(
  url: string,
  getRequestBody?: (params: GetRequestBodyParams) => Record<string, unknown>,
  options?: AgGridDatasourceOptions,
): IServerSideDatasource {
  const booleanFieldSet = new Set(options?.booleanFields ?? []);
  const staleTime = options?.staleTime ?? 86_400_000;

  return {
    getRows: async params => {
      try {
        // Extract sorting
        const { sortBy, sortOrder } = parseSortModel(params.request.sortModel ?? []);

        // Extract filtering with enhanced parsing
        const filterModel = (params.request.filterModel ?? {}) as Record<string, AgFilterConfig | null | undefined>;
        const filters = parseFilterModel(filterModel, booleanFieldSet);

        // Extract pagination
        const skip = params.request.startRow ?? 0;
        const limit = (params.request.endRow ?? 5) - skip;

        // Build request body with custom formatter if provided
        const requestBody: Record<string, unknown> = getRequestBody
          ? getRequestBody({ skip, limit, sortBy, sortOrder, filters })
          : {
              skip,
              limit,
              sort_by: sortBy,
              sort_order: sortOrder,
              filters: Object.keys(filters).length > 0 ? filters : undefined,
            };

        // Use TanStack Query's cache so that re-navigating to a page that has
        // already loaded this grid avoids the loading overlay entirely.
        // fetchQuery resolves immediately when the cached entry is still fresh
        // (within `staleTime`), making getRows appear synchronous to AG Grid.
        const response = await queryClient.fetchQuery<TResponse | null>({
          queryKey: ['ag-grid-datasource', url, requestBody],
          queryFn: () => apiClient.post<TResponse>(url, requestBody),
          staleTime,
        });

        params.success({
          rowCount: response?.total ?? 0,
          rowData: response?.records ?? [],
        });
      } catch (error) {
        console.error('AG Grid datasource fetch error:', error);
        params.fail();
      }
    },
  };
}
